import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { forumApi, resolveImageUrl } from '../lib/api';
import { getCrownVicImage } from '../lib/images';
import { Trophy, ChevronLeft, ChevronRight, User } from 'lucide-react';

export default function RaceWinsSlideshow() {
  const [photos, setPhotos] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPhotos = async () => {
      try {
        const res = await forumApi.getRaceWinsPhotos(20);
        setPhotos(res.data);
      } catch (e) {
        console.error('Error fetching race wins photos:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchPhotos();
  }, []);

  // Auto-cycle every 5 seconds
  useEffect(() => {
    if (photos.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % photos.length);
    }, 5000);
    return () => clearInterval(interval);
  }, [photos.length]);

  const goTo = useCallback((index) => {
    setCurrentIndex(index);
  }, []);

  const goPrev = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? photos.length - 1 : prev - 1));
  }, [photos.length]);

  const goNext = useCallback(() => {
    setCurrentIndex((prev) => (prev + 1) % photos.length);
  }, [photos.length]);

  if (loading) {
    return (
      <div className="bg-[#141414] rounded-lg border border-[#262626] p-8 text-center">
        <div className="w-8 h-8 border-3 border-[#FF3B30] border-t-transparent rounded-full animate-spin mx-auto"></div>
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="bg-[#141414] rounded-lg border border-[#262626] overflow-hidden">
        <div className="p-6 text-center">
          <Trophy className="w-12 h-12 mx-auto text-[#FF3B30] mb-3 opacity-50" />
          <h3 className="font-heading font-bold text-lg text-white mb-2">Race Winners Spotlight</h3>
          <p className="text-gray-500 text-sm mb-4">
            No winner photos yet. Post your wins in the{' '}
            <Link to="/forum?category=race-wins" className="text-[#FF3B30] hover:underline">Race Wins</Link>{' '}
            forum with photos!
          </p>
        </div>
      </div>
    );
  }

  const current = photos[currentIndex];

  return (
    <div className="bg-[#141414] rounded-lg border border-[#262626] overflow-hidden" data-testid="race-wins-slideshow">
      {/* Header */}
      <div className="flex items-center justify-between px-5 py-3 border-b border-[#262626]">
        <div className="flex items-center gap-2">
          <Trophy className="w-5 h-5 text-[#FF3B30]" />
          <h3 className="font-heading font-bold text-white text-sm uppercase tracking-wider">Race Winners</h3>
        </div>
        <Link
          to="/forum?category=race-wins"
          className="text-xs text-gray-500 hover:text-[#FF3B30] transition-colors"
          data-testid="view-all-wins-link"
        >
          View All
        </Link>
      </div>

      {/* Image */}
      <div className="relative aspect-[16/10] bg-black">
        <img
          src={resolveImageUrl(current.image_url) || getCrownVicImage(current.thread_id)}
          alt={current.title}
          className="w-full h-full object-contain"
          onError={(e) => { e.target.src = getCrownVicImage(current.thread_id); }}
        />

        {/* Gradient overlay at bottom */}
        <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/80 to-transparent"></div>

        {/* Caption */}
        <div className="absolute bottom-0 left-0 right-0 p-4">
          <Link to={`/forum/thread/${current.thread_id}`} className="hover:text-[#FF3B30] transition-colors">
            <h4 className="text-white font-medium text-sm line-clamp-1">{current.title}</h4>
          </Link>
          <p className="text-gray-400 text-xs flex items-center gap-1 mt-1">
            <User className="w-3 h-3" />
            {current.author_name}
          </p>
        </div>

        {/* Nav Arrows */}
        {photos.length > 1 && (
          <>
            <button
              onClick={goPrev}
              className="absolute left-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="slideshow-prev"
            >
              <ChevronLeft className="w-5 h-5" />
            </button>
            <button
              onClick={goNext}
              className="absolute right-3 top-1/2 -translate-y-1/2 w-8 h-8 bg-black/50 hover:bg-black/70 rounded-full flex items-center justify-center text-white transition-colors"
              data-testid="slideshow-next"
            >
              <ChevronRight className="w-5 h-5" />
            </button>
          </>
        )}
      </div>

      {/* Dots */}
      {photos.length > 1 && (
        <div className="flex items-center justify-center gap-1.5 py-3 bg-[#0A0A0A]">
          {photos.map((_, idx) => (
            <button
              key={idx}
              onClick={() => goTo(idx)}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-[#FF3B30] w-4' : 'bg-gray-600 hover:bg-gray-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
